// Server function behind the back office's initial data: the open tab's first page (or report),
// fetched as the visitor so it renders in the document. Non-administrators get nothing (the page
// renders "Access denied" for them).

import { createServerFn } from "@tanstack/react-start";
import type { ResponseOf } from "api-def";
import { isSystemAdmin } from "shared/User";
import {
  getAdminAnalytics,
  getAdminComplianceCases,
  getAdminOrganisations,
  getAdminProjects,
  getAdminSafetyHolds,
  getAdminSubprocessorNotices,
  getAdminSupportTickets,
  getAdminUsers,
  getAdminWaitlist,
} from "src/CalmLensApi";
import { fetchAuthState } from "src/lib/ServerUtils";

/** Page size of every back office list (the lists request the same). */
const ADMIN_PAGE_SIZE = 20;
/** The analytics tab's default period, in days (AdminAnalytics starts with the same). */
const ADMIN_ANALYTICS_DEFAULT_DAYS = 30;
/** The waitlist tab's default status filter (AdminWaitlistList starts with the same). */
const ADMIN_WAITLIST_DEFAULT_STATUS = "pending" as const;

const firstPage = { pageIndex: 0, pageSize: ADMIN_PAGE_SIZE };

export type AdminPageData =
  | { tab: "compliance"; data: ResponseOf<typeof getAdminComplianceCases> }
  | { tab: "tickets"; data: ResponseOf<typeof getAdminSupportTickets> }
  | { tab: "safety-holds"; data: ResponseOf<typeof getAdminSafetyHolds> }
  | { tab: "users"; data: ResponseOf<typeof getAdminUsers> }
  | { tab: "projects"; data: ResponseOf<typeof getAdminProjects> }
  | { tab: "organisations"; data: ResponseOf<typeof getAdminOrganisations> }
  | { tab: "waitlist"; data: ResponseOf<typeof getAdminWaitlist> }
  | { tab: "subprocessors"; data: ResponseOf<typeof getAdminSubprocessorNotices> }
  | { tab: "analytics"; data: ResponseOf<typeof getAdminAnalytics> }
  | { tab: "none" };

const loadTab = async (
  tab: string,
  userId?: string,
  caseId?: string,
  assetId?: string,
  noticeId?: string,
): Promise<AdminPageData> => {
  switch (tab) {
    case "compliance":
      return {
        tab,
        data: (
          await getAdminComplianceCases.submit({
            query: { ...firstPage, ...(caseId ? { caseId } : {}) },
          })
        ).data,
      };
    case "tickets":
      return { tab, data: (await getAdminSupportTickets.submit({ query: firstPage })).data };
    case "safety-holds":
      return {
        tab,
        data: (await getAdminSafetyHolds.submit({ query: { ...firstPage, assetId } })).data,
      };
    case "users":
      return { tab, data: (await getAdminUsers.submit({ query: { ...firstPage, userId } })).data };
    case "projects":
      return { tab, data: (await getAdminProjects.submit({ query: firstPage })).data };
    case "organisations":
      return { tab, data: (await getAdminOrganisations.submit({ query: firstPage })).data };
    case "waitlist":
      return {
        tab,
        data: (
          await getAdminWaitlist.submit({
            query: { ...firstPage, status: ADMIN_WAITLIST_DEFAULT_STATUS },
          })
        ).data,
      };
    case "subprocessors":
      return {
        tab,
        data: (await getAdminSubprocessorNotices.submit({ query: { ...firstPage, noticeId } }))
          .data,
      };
    case "analytics":
      return {
        tab,
        data: (await getAdminAnalytics.submit({ query: { days: ADMIN_ANALYTICS_DEFAULT_DAYS } }))
          .data,
      };
    default:
      // The development-only pricing tab renders in the browser.
      return { tab: "none" };
  }
};

/** `/admin?tab=`: the open tab's initial data, for system administrators only. */
export const getAdminPageData = createServerFn({ method: "GET" })
  .validator(
    (input: {
      tab: string;
      userId?: string;
      caseId?: string;
      assetId?: string;
      noticeId?: string;
    }) => input,
  )
  .handler(async ({ data: { tab, userId, caseId, assetId, noticeId } }): Promise<AdminPageData> => {
    const authState = await fetchAuthState();
    if (!isSystemAdmin(authState?.user)) return { tab: "none" };
    return loadTab(tab, userId, caseId, assetId, noticeId);
  });
